import type { FieldState } from '../types/fieldState';
import type { WeatherRisk } from '../types/weatherRisk';
import { RISK_TYPE_ORDER, RISK_TYPE_ORDER_B } from './riskDisplay';

/**
 * ステート別カレンダー表示設定
 * FIELD_CALENDAR_SPEC.md のUI表示アイテム一覧（調整後）に基づく
 */

/** リスク情報グループ */
export type RiskGroup = 'A' | 'B';

/**
 * リスクグループごとの表示リスク種別（種別リストの定義元を一箇所に集約）。
 * 各ステートの riskTypes はこのテーブルを参照することで、group-B に種別を追加した際の
 * 各ステート配列への追加漏れといった二重管理ハザードを防ぐ。
 *
 * 種別名（'crust'/'frost'/'wind'/'heavy_rain'/'div'）は、これらが
 * `/risks?riskTypes=...` のリクエスト契約として BE の RiskType.paramName
 * （backend/.../harc/RiskType.java）に渡るため、両者を一致させること。値がずれると実行時 400 になる。
 */
export const RISK_GROUP_RISK_TYPES = {
  A: ['crust', 'frost', 'wind', 'heavy_rain'],
  B: ['div', 'wind', 'heavy_rain'],
} as const satisfies Record<RiskGroup, readonly string[]>;

/** アイコン表示種別 */
export type IconDisplayType = 'scheduled' | 'actual' | 'hidden';

/** ステート別表示設定 */
export interface StateDisplayConfig {
  /** 表示するリスクグループ */
  riskGroup: RiskGroup;
  /** 表示するリスクタイプ一覧 */
  riskTypes: readonly string[];
  /** 播種適正範囲表示（播種バー帯 + ◯△✗ABC）と出芽予測範囲表示（出芽バー帯 + 🌱ABC）の表示可否 */
  showSeedingRange: boolean;
  /** 防除バーの表示可否 */
  showPestControlBar: boolean;
  /** 播種アイコン表示種別 */
  seedingIcon: IconDisplayType;
  /** 出芽アイコン表示種別 */
  emergenceIcon: IconDisplayType;
  /** 防除実施日アイコン表示可否 */
  showPestControlActualIcon: boolean;
  /** 収穫予定日アイコン表示可否 */
  showHarvestScheduleIcon: boolean;
}

/** ステート別表示設定マップ */
export const STATE_DISPLAY_CONFIG: Record<FieldState, StateDisplayConfig> = {
  BEFORE_SEEDING: {
    riskGroup: 'A',
    riskTypes: RISK_GROUP_RISK_TYPES.A,
    showSeedingRange: true, // 追加条件: 4/1以降 AND 播種予定日登録済み
    showPestControlBar: false,
    seedingIcon: 'scheduled',
    emergenceIcon: 'scheduled', // 播種予定日登録時に出芽予測バーも表示
    showPestControlActualIcon: false,
    showHarvestScheduleIcon: false,
  },
  BEFORE_EMERGENCE: {
    riskGroup: 'A',
    riskTypes: RISK_GROUP_RISK_TYPES.A,
    showSeedingRange: false,
    showPestControlBar: false,
    seedingIcon: 'actual',
    emergenceIcon: 'scheduled',
    showPestControlActualIcon: false,
    showHarvestScheduleIcon: false,
  },
  INITIAL_GROWTH: {
    riskGroup: 'A',
    riskTypes: RISK_GROUP_RISK_TYPES.A,
    showSeedingRange: false,
    showPestControlBar: false,
    seedingIcon: 'actual',
    emergenceIcon: 'actual',
    showPestControlActualIcon: false,
    showHarvestScheduleIcon: false,
  },
  PEST_CONTROL: {
    riskGroup: 'B',
    riskTypes: RISK_GROUP_RISK_TYPES.B,
    showSeedingRange: false,
    showPestControlBar: true,
    seedingIcon: 'actual',
    emergenceIcon: 'actual',
    showPestControlActualIcon: true,
    showHarvestScheduleIcon: false,
  },
  HARVESTABLE: {
    riskGroup: 'B',
    riskTypes: RISK_GROUP_RISK_TYPES.B,
    showSeedingRange: false,
    showPestControlBar: false,
    seedingIcon: 'actual',
    emergenceIcon: 'actual',
    showPestControlActualIcon: true,
    showHarvestScheduleIcon: true,
  },
};

/** 全リスクタイプ（フォールバック用） */
const ALL_RISK_TYPES = ['crust', 'frost', 'wind', 'heavy_rain', 'div'] as const;

/**
 * ステートの表示設定を取得する
 * @param state 圃場ステート（undefined の場合はフォールバック）
 * @returns ステートに対応する表示設定
 */
export function getDisplayConfig(state: FieldState | undefined): StateDisplayConfig {
  if (!state) {
    // フォールバック: 全リスク表示
    return {
      riskGroup: 'A',
      riskTypes: RISK_GROUP_RISK_TYPES.A,
      showSeedingRange: false,
      showPestControlBar: false,
      seedingIcon: 'scheduled',
      emergenceIcon: 'hidden',
      showPestControlActualIcon: false,
      showHarvestScheduleIcon: false,
    };
  }
  return STATE_DISPLAY_CONFIG[state];
}

/**
 * ステートに応じた表示対象リスクタイプを取得
 * @param state 圃場ステート（undefined の場合は全リスクタイプを返す）
 * @returns 表示対象のリスクタイプ配列
 */
export function getVisibleRiskTypes(state: FieldState | undefined): readonly string[] {
  if (!state) {
    return ALL_RISK_TYPES;
  }
  return STATE_DISPLAY_CONFIG[state].riskTypes;
}

/**
 * リスクデータをステートでフィルタリング
 * @param risks リスクデータ配列
 * @param state 圃場ステート（undefined の場合はフィルタリングなし）
 * @returns フィルタリング済みのリスクデータ配列
 */
export function filterRisksByState(
  risks: WeatherRisk[],
  state: FieldState | undefined
): WeatherRisk[] {
  if (!state) {
    return risks;
  }
  const visibleTypes = STATE_DISPLAY_CONFIG[state].riskTypes;
  return risks.filter((risk) => visibleTypes.includes(risk.riskType));
}

/**
 * ステートに応じたリスクソート順マップを取得
 * @param state 圃場ステート（undefined の場合は RISK_TYPE_ORDER）
 * @returns リスクタイプのソート順マップ
 */
export function getRiskSortOrder(state: FieldState | undefined): Record<string, number> {
  if (!state) {
    return RISK_TYPE_ORDER;
  }
  const config = STATE_DISPLAY_CONFIG[state];
  return config.riskGroup === 'B' ? RISK_TYPE_ORDER_B : RISK_TYPE_ORDER;
}
